import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createEntrySchema,
  type EntryResponse,
  type EntryValue,
  type TableColumn,
  lookupOptions,
  type TableResponse,
  updateEntrySchema,
} from '@k-management/shared';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';

/** Add an entry to the version being edited, or edit one (the code never changes). */
export function EntryDialog({
  table,
  version,
  entry,
  initialValues,
  initialDescription,
  readOnly,
  onClose,
  children,
}: {
  table: TableResponse;
  version: number;
  entry: EntryResponse | null;
  /** Values of a new entry opened from another table. */
  initialValues?: Record<string, EntryValue>;
  /** Description of a new entry made from another (a voice of the Base personalised). */
  initialDescription?: string;
  readOnly: boolean;
  onClose: () => void;
  /** What the form shows under the fields, e.g. the costs of a type of line. */
  children?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [code, setCode] = useState(entry?.code ?? '');
  const [description, setDescription] = useState(entry?.description ?? initialDescription ?? '');
  const [active, setActive] = useState(entry?.active ?? true);
  const [isDefault, setIsDefault] = useState(entry?.isDefault ?? false);
  const [hidden, setHidden] = useState(entry?.hidden ?? false);
  const [values, setValues] = useState<Record<string, EntryValue>>(
    entry?.values ?? initialValues ?? {},
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const save = useMutation({
    mutationFn: (body: unknown) =>
      entry
        ? apiRequest<EntryResponse>(
            `/tables/${table.key}/versions/${version}/entries/${encodeURIComponent(entry.code)}`,
            { method: 'PATCH', body },
          )
        : apiRequest<EntryResponse>(`/tables/${table.key}/entries`, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  // Only an entry nothing uses goes: the server says where a used one is (28/09/2026).
  const remove = useMutation({
    mutationFn: () =>
      apiRequest<void>(
        `/tables/${table.key}/versions/${version}/entries/${encodeURIComponent(entry!.code)}`,
        { method: 'DELETE' },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      onClose();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const body = entry
      ? { description, active, isDefault, hidden, values }
      : { code, description, active, isDefault, hidden, values };
    const result = entry ? validate(updateEntrySchema, body) : validate(createEntrySchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) save.mutate(body);
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <h2>
          {entry ? `${table.name} · ${entry.code}` : `Nuova voce · ${table.name}`}
          {!readOnly && <small className="muted"> (versione {version})</small>}
        </h2>
        <div className="form-grid">
          <Field
            label="Codice"
            error={errors.code}
            hint={
              entry
                ? 'Il codice non cambia: è ciò che i dati collegati ricordano.'
                : table.autoCode
                  ? 'Si assegna da solo al salvataggio; puoi anche scriverne uno. Non si potrà cambiare.'
                  : 'Non si potrà cambiare.'
            }
          >
            <input
              value={code}
              disabled={Boolean(entry)}
              placeholder={!entry && table.autoCode ? `${table.autoCode}-…` : undefined}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Descrizione" error={errors.description}>
            <input
              value={description}
              disabled={readOnly}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          {table.columns.map((column) => (
            <ValueField
              key={column.key}
              column={column}
              value={values[column.key] ?? null}
              error={errors[`values.${column.key}`]}
              disabled={readOnly}
              {...(column.lookup
                ? {
                    // An entry is never its own parent.
                    options: lookupOptions(column, values, table.lookups).filter(
                      (option) => column.lookup!.table !== table.key || option.code !== entry?.code,
                    ),
                    ...(column.lookup.table === table.key && {
                      emptyLabel: '— nessuna: voce di primo livello —',
                    }),
                  }
                : {})}
              onChange={(value) =>
                setValues((current) => {
                  const next = { ...current, [column.key]: value };
                  // A new voice empties its sub-voices: they belonged to the old one.
                  for (const child of table.columns) {
                    if (child.lookup?.childOf === column.key) next[child.key] = null;
                  }
                  return next;
                })
              }
            />
          ))}
        </div>
        <div className="checkbox-row">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={active}
              disabled={readOnly || entry?.locked}
              onChange={(e) => setActive(e.target.checked)}
            />
            Attiva
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={isDefault}
              disabled={readOnly}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Predefinita
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={hidden}
              disabled={readOnly}
              onChange={(e) => setHidden(e.target.checked)}
            />
            Nascosta negli elenchi di scelta
          </label>
        </div>
        {entry?.locked && (
          <p className="muted small">
            Voce di base: i moduli ci costruiscono regole, quindi resta attiva. Descrizione e
            colonne si modificano.
          </p>
        )}
        {children}
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          {entry && !readOnly && !entry.locked && (
            <button type="button" className="danger" onClick={() => setConfirmDelete(true)}>
              Elimina
            </button>
          )}
          <button type="button" onClick={onClose}>
            {readOnly ? 'Chiudi' : 'Annulla'}
          </button>
          {!readOnly && (
            <button type="submit" className="primary" disabled={save.isPending}>
              {save.isPending ? 'Salvataggio…' : entry ? 'Salva' : 'Aggiungi'}
            </button>
          )}
        </div>
      </form>
      {confirmDelete && entry && (
        <ConfirmDialog
          title={`Eliminare ${entry.code} «${entry.description}»?`}
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => {
            remove.reset();
            setConfirmDelete(false);
          }}
        >
          <p>
            La voce sparisce dalla tabella e l’eliminazione resta nello storico. Se è usata da
            soggetti, preventivi o altre tabelle non si elimina: si disattiva togliendo «Attiva».
          </p>
        </ConfirmDialog>
      )}
    </dialog>
  );
}

function ValueField({
  column,
  value,
  error,
  disabled,
  options,
  emptyLabel,
  onChange,
}: {
  column: TableColumn;
  value: EntryValue;
  error?: string;
  disabled: boolean;
  /** The entries of another table this column takes its codes from. */
  options?: { code: string; description: string }[];
  /** The choice for no value, when «Scegli…» would not say what it means. */
  emptyLabel?: string;
  onChange: (value: EntryValue) => void;
}) {
  if (column.lookup && options) {
    const current = value === null ? '' : String(value);
    const known = options.some((option) => option.code === current);
    return (
      <Field label={column.label} error={error}>
        <select
          value={current}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">
            {emptyLabel ?? (column.lookup.childOf ? '— tutte —' : 'Scegli…')}
          </option>
          {options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.description}
            </option>
          ))}
          {current && !known && <option value={current}>{current} (non più in tabella)</option>}
        </select>
      </Field>
    );
  }
  switch (column.type) {
    case 'boolean':
      return (
        <label className="checkbox field-checkbox">
          <input
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          {column.label}
        </label>
      );
    case 'select':
      return (
        <Field label={column.label} error={error}>
          <select
            value={value === null ? '' : String(value)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="" />
            {column.options?.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </Field>
      );
    case 'tags': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <fieldset className="field-tags">
          <legend>{column.label}</legend>
          {column.options?.map((option) => (
            <label key={option} className="checkbox">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                disabled={disabled}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, option]
                      : selected.filter((item) => item !== option),
                  )
                }
              />
              {option}
            </label>
          ))}
          {error && <span className="field-error">{error}</span>}
        </fieldset>
      );
    }
    case 'number':
    case 'money':
      return (
        <Field label={column.label} error={error}>
          <DecimalInput
            decimals={column.type === 'money' ? [2, 2] : [0, 4]}
            value={value === null ? '' : String(value)}
            disabled={disabled}
            onChange={(text) => onChange(text)}
          />
        </Field>
      );
    default:
      return (
        <Field label={column.label} error={error}>
          <input
            type={column.type === 'date' ? 'date' : 'text'}
            value={value === null ? '' : String(value)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
  }
}
